import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { usePageMeta } from '@/hooks/usePageMeta';
import { faqs } from '@/mocks/faqs';
import { site } from '@/mocks/site';
import type { FaqItem } from '@/types/content';

const list = faqs as FaqItem[];

export default function Faq() {
  const [open, setOpen] = useState<number | null>(0);

  const schema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: list.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    }),
    [],
  );

  usePageMeta({
    title: '常见问题 - 福利吃瓜更新节奏与订阅说明',
    description:
      '「福利吃瓜」常见问题解答：内容更新频率、今日上新与本周推荐的区别、周末限定与假日特辑安排、订阅档期提醒方式及内容纠错反馈渠道。',
    keywords: '福利吃瓜常见问题,福利吃瓜订阅,福利吃瓜更新时间,福利吃瓜栏目说明',
    path: '/faq',
    schema,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main className="pt-16 md:pt-20">
        <section className="bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
            <Breadcrumbs items={[{ label: '常见问题' }]} />
            <h1 className="mt-5 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
              常见问题
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl leading-7">
              关于更新时间、栏目区别、订阅方式与转载授权，答案都在这里。如果还有疑问，欢迎通过下方邮箱联系我们。
            </p>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-8">
                <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
                  {list.map((item, i) => {
                    const isOpen = open === i;
                    return (
                      <div key={item.q} className="border-b border-background-200 last:border-0">
                        <h2>
                          <button
                            type="button"
                            onClick={() => setOpen(isOpen ? null : i)}
                            aria-expanded={isOpen}
                            className="w-full flex items-center justify-between gap-4 px-5 md:px-7 py-5 text-left hover:bg-background-100 transition-colors"
                          >
                            <span className="flex items-start gap-3">
                              <span className="font-heading text-sm text-primary-500 font-bold mt-0.5">
                                {String(i + 1).padStart(2, '0')}
                              </span>
                              <span className="font-heading text-sm md:text-base font-bold text-foreground-950">
                                {item.q}
                              </span>
                            </span>
                            <span
                              className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                                isOpen ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-700'
                              }`}
                            >
                              <i
                                className={`${isOpen ? 'ri-subtract-line' : 'ri-add-line'} text-base`}
                                aria-hidden="true"
                              ></i>
                            </span>
                          </button>
                        </h2>
                        {isOpen && (
                          <div className="px-5 md:px-7 pb-6 pl-[52px] md:pl-[68px]">
                            <p className="text-sm text-foreground-600 leading-7">{item.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <aside className="lg:col-span-4 space-y-5 lg:sticky lg:top-28 h-fit">
                <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                  <h2 className="font-heading text-base font-bold text-foreground-950 mb-4">快速入口</h2>
                  <ul className="space-y-2">
                    {[
                      { to: '/topics?cat=today', label: '今日上新' },
                      { to: '/topics?cat=weekly', label: '本周推荐' },
                      { to: '/topics?cat=weekend', label: '周末限定' },
                      { to: '/topics?cat=holiday', label: '假日特辑' },
                      { to: '/topics?cat=hot', label: '近期热点' },
                    ].map((l) => (
                      <li key={l.to}>
                        <Link
                          to={l.to}
                          className="flex items-center justify-between py-2.5 text-sm text-foreground-700 hover:text-primary-600 transition-colors"
                        >
                          {l.label}
                          <i className="ri-arrow-right-line text-foreground-400" aria-hidden="true"></i>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-lg bg-primary-500 p-6">
                  <h2 className="font-heading text-base font-bold text-background-50">还没解决的问题？</h2>
                  <p className="mt-2 text-sm text-primary-50 leading-7">
                    发邮件给我们，说明具体情况，我们会尽快回复。
                  </p>
                  <a
                    href={`mailto:${site.email}`}
                    className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50 text-primary-700 text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors"
                  >
                    <i className="ri-mail-send-line" aria-hidden="true"></i>
                    发送邮件
                  </a>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}